import { PantallaCargando } from '@/components/Cargando';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle, Eye, FileCode, FilePlus, Image as ImageIcon, Loader2,
  BarChart3, ClipboardList, Globe, History,
  Monitor, RefreshCw, Rocket, Save, Smartphone, Tablet, Trash2, Upload, X,
} from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { usePermisos } from '@/lib/permisos';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * Editor de código del sitio — S77.
 *
 * Reemplaza al lienzo de arrastrar y soltar. El dueño del sitio ve sus archivos
 * tal como los escribió y los edita directamente: nada se interpreta, nada se
 * reacomoda solo. Lo que se guarda es exactamente lo que se publica.
 *
 * Se usa un textarea con tipografía monoespaciada en vez de una librería de
 * editor: no hay ninguna instalada, y traer una nueva por resaltado de sintaxis
 * no compensa el riesgo en el build. El manejo de Tab sí está resuelto, que es
 * lo que de verdad estorba al escribir código en un textarea.
 */

// Tamaños reales de pantalla. La vista previa se renderiza a estas medidas y
// luego se REDUCE para caber: así el sitio responde con el diseño que de verdad
// le tocaría en ese dispositivo, en vez de creer que está en una ventana
// angosta y saltar a su versión móvil.
const DISPOSITIVOS = {
  escritorio: { nombre: 'Escritorio', w: 1440, h: 900, icono: Monitor },
  tablet: { nombre: 'Tablet', w: 820, h: 1180, icono: Tablet },
  celular: { nombre: 'Celular', w: 390, h: 844, icono: Smartphone },
};

// Debe coincidir con MAX_RELEASES_POR_SITIO del backend (services/web/cleanup.py).
// Aquí solo se usa para explicárselo al dueño; quien la aplica es el servidor.
const MAX_VERSIONES = 5;

const isImage = (ct) => (ct || '').startsWith('image/');

function formatBytes(n) {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function WebSiteEditor() {
  const { siteId } = useParams();
  // Quién puede hacer qué. El servidor lo vuelve a comprobar en cada petición;
  // esto solo evita que alguien escriba media hora de código para descubrir al
  // guardar que no le tocaba. Ver lib/permisos.js.
  const { puedeEditar } = usePermisos();
  const puedeGuardarCodigo = puedeEditar('web_custom_code');
  const puedeSubirSitio = puedeEditar('web_import');
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);   // ruta abierta
  const [body, setBody] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [preview, setPreview] = useState(false);
  // Subir una plantilla completa tarda: son cientos de archivos. Sin un estado
  // visible la pantalla se queda quieta y parece que el botón no hizo nada.
  const [subiendo, setSubiendo] = useState(false);
  // El archivo elegido, esperando confirmación. Antes se usaba window.confirm,
  // y Chrome deja de mostrar esos diálogos cuando una pestaña abre varios
  // seguidos: a partir de ahí devuelve «cancelar» sin preguntar y el botón
  // deja de responder sin ningún aviso. Justo lo que pasó aquí.
  const [zipPendiente, setZipPendiente] = useState(null);
  // Diálogo propio para pedir un dato o confirmar. Los nativos del
  // navegador (`prompt`/`confirm`) dejan de aparecer cuando una pestaña abre
  // varios seguidos: Chrome los resuelve solo como «cancelar» y el botón
  // deja de responder sin ningún aviso. Ya pasó con «Subir mi sitio».
  const [dialogo, setDialogo] = useState(null);
  // Cambia con cada guardado para que el iframe recargue en vez de mostrar
  // caché: ver el cambio es justamente el objetivo de la vista previa.
  const [previewNonce, setPreviewNonce] = useState(0);
  const textareaRef = useRef(null);
  const uploadRef = useRef(null);
  const assetRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tenant/web/sites/${siteId}`);
      setSite(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return null;
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const openFile = useCallback(async (path) => {
    try {
      // Cambiar de archivo GUARDA lo que había. Antes preguntaba «¿descartar
      // los cambios?» y un clic distraído tiraba el trabajo sin recuperación
      // posible. Nadie que hace clic en otro archivo está pidiendo perder lo
      // que escribió.
      if (dirty && current) {
        await api.put(`/tenant/web/sites/${siteId}/files/${current}`, { body });
        toast.success(`Guardado ${current}`);
      }
      const { data } = await api.get(`/tenant/web/sites/${siteId}/files/${path}`);
      setCurrent(data.path);
      setBody(data.body || '');
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [siteId, dirty, current, body]);

  useEffect(() => {
    (async () => {
      const data = await load();
      const first = data?.files?.find((f) => f.path === 'index.html') || data?.files?.[0];
      if (first) {
        try {
          const { data: f } = await api.get(`/tenant/web/sites/${siteId}/files/${first.path}`);
          setCurrent(f.path);
          setBody(f.body || '');
        } catch { /* la lista ya se muestra; el archivo se abre al hacer clic */ }
      }
    })();
    // solo al montar: openFile depende de `dirty` y recargaría de más
  }, [siteId, load]);

  // Avisar antes de cerrar la pestaña con cambios sin guardar.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = useCallback(async () => {
    if (!current) return;
    setSaving(true);
    try {
      await api.put(`/tenant/web/sites/${siteId}/files/${current}`, { body });
      setDirty(false);
      setPreviewNonce((n) => n + 1);
      toast.success('Guardado');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }, [siteId, current, body, load]);

  // Ctrl/Cmd+S guarda, como en cualquier editor.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); save(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save]);

  // Tab inserta dos espacios en vez de saltar de campo — sin esto no se puede
  // escribir código con comodidad en un textarea.
  const onKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const el = e.target;
    const { selectionStart: s, selectionEnd: en } = el;
    const next = `${body.slice(0, s)}  ${body.slice(en)}`;
    setBody(next);
    setDirty(true);
    requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = s + 2; });
  };

  const newFile = () => setDialogo({
    titulo: 'Archivo nuevo',
    descripcion: 'Puede ir dentro de una carpeta. Por ejemplo: contacto.html o css/estilo.css',
    valor: '',
    marcador: 'contacto.html',
    boton: 'Crear',
    onAceptar: async (path) => {
      try {
        await api.put(`/tenant/web/sites/${siteId}/files/${path.trim()}`, { body: '' });
        toast.success('Archivo creado');
        await load();
        openFile(path.trim().replace(/^\/+/, ''));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    },
  });

  const removeFile = (path) => setDialogo({
    titulo: `¿Eliminar ${path}?`,
    descripcion: 'No se puede deshacer. Tu sitio en internet no cambia hasta que vuelvas a publicar.',
    peligro: true,
    boton: 'Sí, eliminar',
    onAceptar: () => borrarArchivo(path),
  });

  const borrarArchivo = async (path) => {
    try {
      await api.delete(`/tenant/web/sites/${siteId}/files/${path}`);
      toast.success('Archivo eliminado');
      if (current === path) { setCurrent(null); setBody(''); setDirty(false); }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const uploadZip = async (file) => {
    if (!file) return;
    setZipPendiente(null);
    setSubiendo(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      // El cliente `api` pone Content-Type: application/json en TODA petición.
      // Con un FormData eso pisa el boundary que el navegador tiene que generar
      // solo, y al backend le llega un cuerpo que no puede leer: responde
      // "Field required" como si no hubieras adjuntado nada. Ya había pasado en
      // Conocimiento (Sprint 37); limpiarlo aquí deja que el navegador ponga el
      // encabezado correcto.
      const { data } = await api.post(`/tenant/web/sites/${siteId}/upload`, fd, {
        headers: { 'Content-Type': undefined },
      });
      const partes = [`${data.files} archivos`, `${data.assets} imágenes`];
      const nForms = (data.forms && data.forms.connected && data.forms.connected.length) || 0;
      if (nForms) partes.push(`${nForms} formulario${nForms === 1 ? '' : 's'} conectado${nForms === 1 ? '' : 's'}`);
      const nCamp = data.forms?.campaigns_created || 0;
      if (nCamp) partes.push(`${nCamp} campaña${nCamp === 1 ? '' : 's'}`);
      if (data.forms?.ai_assisted) partes.push('IA ayudó al mapear');
      toast.success(`Sitio cargado: ${partes.join(', ')}`);
      // Lo que no se pudo subir se dice, no se esconde.
      (data.skipped || []).slice(0, 5).forEach(
        (s) => toast.warning(`No se subió ${s.path}: ${s.reason}`));
      if ((data.skipped || []).length > 5) {
        toast.warning(`…y ${data.skipped.length - 5} archivos más sin subir`);
      }
      (data.failed_assets || []).forEach(
        (s) => toast.error(`Falló ${s.path}: ${s.reason}`));
      (data.forms?.connected || []).slice(0, 6).forEach((f) => {
        toast.message(`Formulario listo en ${f.page}`, {
          description: `Campos: ${(f.fields || []).join(', ') || '—'}`,
        });
      });
      setCurrent(null); setBody(''); setDirty(false);
      setPreviewNonce((n) => n + 1);
      load();
    } catch (e) {
      // Un fallo de red no trae `detail`, y sin este respaldo el aviso salía
      // vacío: el usuario veía la pantalla igual y concluía, con razón, que el
      // botón no hacía nada.
      toast.error(e.response
        ? formatApiError(e.response?.data?.detail)
        : 'No pudimos contactar al servidor. Revisa tu conexión y vuelve a intentarlo.');
    } finally {
      setSubiendo(false);
    }
  };

  const uploadAsset = (file) => {
    if (!file) return;
    setDialogo({
      titulo: 'Subir imagen',
      descripcion: 'Con este nombre la vas a llamar desde tu HTML. Puedes dejarlo como está.',
      valor: `img/${file.name}`,
      boton: 'Subir',
      onAceptar: (path) => guardarImagen(file, path),
    });
  };

  const guardarImagen = async (file, path) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(
        `/tenant/web/sites/${siteId}/assets?path=${encodeURIComponent(path.trim())}`, fd,
        { headers: { 'Content-Type': undefined } });
      toast.success(`Listo. Úsala en tu HTML como /${path.trim().replace(/^\/+/, '')}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      // Publicar GUARDA primero. Antes preguntaba «tienes cambios sin guardar;
      // no se publicarán, ¿continuar?» y al aceptar publicaba la versión
      // anterior. El dueño hizo eso tres veces seguidas: tres publicaciones
      // con contenido idéntico, y él viendo su sitio sin el cambio sin
      // entender por qué. La pregunta era técnicamente correcta y en la
      // práctica una trampa: quien pulsa Publicar quiere publicar lo que ve.
      if (dirty && current) {
        try {
          await api.put(`/tenant/web/sites/${siteId}/files/${current}`, { body });
          setDirty(false);
        } catch (e) {
          toast.error('No pudimos guardar tu cambio, así que no se publicó nada. '
                      + formatApiError(e.response?.data?.detail));
          return;   // publicar sin el cambio es justo el error que se corrige
        }
      }
      const { data } = await api.post(`/tenant/web/sites/${siteId}/publish`);
      toast.success(`Publicado (versión ${data.version})`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  // El esqueleto inicial son tres archivos; más que eso significa que el dueño
  // ya trajo lo suyo, y entonces el botón deja de decir "subir" y dice
  // "reemplazar", que es lo que de verdad va a pasar.
  const yaTieneSitio = (site?.files?.length || 0) > 3 || (site?.assets?.length || 0) > 0;

  if (loading) return <PantallaCargando texto="Cargando tu sitio…" />;
  if (!site) return <div className="p-6 text-sm text-[#64748B]">No encontramos este sitio.</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4" data-testid="web-code-editor">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/tenant/web" className="text-xs text-[#F72585]">← Sitio Web</Link>
          <h1 className="text-2xl font-bold text-[#0F172A] mt-1">{site.name}</h1>
          <p className="text-sm text-[#64748B]">
            Estos son los archivos de tu sitio. Lo que guardes aquí es exactamente
            lo que se publica: no cambiamos tu código.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Algunos sistemas reportan el .zip con otro tipo y el selector lo
              deja gris. Se aceptan las variantes en vez de dejar al dueño
              peleándose con un archivo que sí es válido. */}
          <input ref={uploadRef} type="file" className="hidden"
                 accept=".zip,application/zip,application/x-zip-compressed,multipart/x-zip"
                 onChange={(e) => { setZipPendiente(e.target.files?.[0] || null); e.target.value = ''; }} />
          <Button size="sm" variant="outline" disabled={subiendo || !puedeSubirSitio}
                  onClick={() => uploadRef.current?.click()}
                  title={puedeSubirSitio ? undefined
                    : 'Tu tipo de usuario no puede subir ni reemplazar el sitio. El dueño lo cambia en Mi Negocio → Permisos.'}
                  data-testid="web-upload-zip">
            {subiendo
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Subiendo tu sitio…</>
              : yaTieneSitio
                ? <><RefreshCw className="w-4 h-4 mr-1.5" /> Reemplazar sitio</>
                : <><Upload className="w-4 h-4 mr-1.5" /> Subir mi sitio (.zip)</>}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPreview(true)}
                  data-testid="web-preview">
            <Eye className="w-4 h-4 mr-1.5" /> Ver cómo va
          </Button>
          <Button size="sm" className="bg-[#F72585] hover:bg-[#D81B70]"
                  onClick={publish} disabled={publishing} data-testid="web-publish">
            {publishing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        : <Rocket className="w-4 h-4 mr-1.5" />}
            Publicar
          </Button>
        </div>
      </div>

      {/* Lo que rodea al sitio: su dominio, su medición, sus formularios y su
          historial. Vivían dentro del constructor retirado y se quedaron sin
          puerta; esta barra es su acceso. */}
      <nav className="flex flex-wrap gap-2 border-y border-[#E2E8F0] py-2">
        {[
          { to: 'domains', icono: Globe, texto: 'Dominio y DNS' },
          { to: 'tracking', icono: BarChart3, texto: 'Medición' },
          { to: 'forms', icono: ClipboardList, texto: 'Formularios' },
          { to: 'versions', icono: History, texto: 'Versiones' },
        ].map(({ to, icono: Icono, texto }) => (
          <Link key={to} to={`/tenant/web/sites/${siteId}/${to}`}
                data-testid={`web-nav-${to}`}
                className="inline-flex items-center gap-1.5 text-sm text-[#475569]
                           hover:text-[#F72585] px-2 py-1 rounded hover:bg-[#FDF2F8]">
            <Icono className="w-4 h-4" /> {texto}
          </Link>
        ))}
      </nav>

      <div className="grid lg:grid-cols-[260px_1fr] gap-4">
        <aside className="space-y-4">
          <div className="border border-[#E2E8F0] rounded-xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#E2E8F0]">
              <span className="text-xs font-semibold text-[#475569] uppercase">Archivos</span>
              {puedeGuardarCodigo && (
                <button type="button" onClick={newFile} title="Archivo nuevo"
                        className="text-[#F72585] hover:opacity-70">
                  <FilePlus className="w-4 h-4" />
                </button>
              )}
            </div>
            <ul className="max-h-[45vh] overflow-y-auto">
              {(site.files || []).map((f) => (
                <li key={f.path}
                    className={`group flex items-center justify-between gap-1 px-3 py-1.5 text-sm
                                ${current === f.path ? 'bg-[#FDF2F8] text-[#0F172A] font-medium'
                                                     : 'text-[#475569] hover:bg-[#F8FAFC]'}`}>
                  <button type="button" onClick={() => openFile(f.path)}
                          className="flex items-center gap-1.5 min-w-0 flex-1 text-left">
                    <FileCode className="w-3.5 h-3.5 shrink-0 opacity-60" />
                    <span className="truncate">{f.path}</span>
                  </button>
                  {puedeGuardarCodigo && (
                    <button type="button" onClick={() => removeFile(f.path)}
                            title="Eliminar" aria-label={`Eliminar ${f.path}`}
                            className="opacity-0 group-hover:opacity-100 text-[#94A3B8] hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-[#E2E8F0] rounded-xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#E2E8F0]">
              <span className="text-xs font-semibold text-[#475569] uppercase">Imágenes</span>
              <input ref={assetRef} type="file" className="hidden"
                     onChange={(e) => { uploadAsset(e.target.files?.[0]); e.target.value = ''; }} />
              <button type="button" onClick={() => assetRef.current?.click()}
                      title="Subir imagen" className="text-[#F72585] hover:opacity-70">
                <ImageIcon className="w-4 h-4" />
              </button>
            </div>
            {!(site.assets || []).length && (
              <p className="px-3 py-3 text-xs text-[#94A3B8]">
                Aún no subes imágenes. Súbelas aquí y úsalas en tu HTML con su ruta.
              </p>
            )}
            <ul className="max-h-[25vh] overflow-y-auto">
              {(site.assets || []).map((a) => (
                <li key={a.path} className="px-3 py-1.5 text-sm text-[#475569] flex items-center gap-1.5">
                  {isImage(a.content_type)
                    ? <ImageIcon className="w-3.5 h-3.5 shrink-0 opacity-60" />
                    : <FileCode className="w-3.5 h-3.5 shrink-0 opacity-60" />}
                  <span className="truncate flex-1">{a.path}</span>
                  <span className="text-[10px] text-[#94A3B8]">{formatBytes(a.bytes)}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <section className="border border-[#E2E8F0] rounded-xl bg-white overflow-hidden flex flex-col">
          <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-[#E2E8F0]">
            <span className="text-sm font-mono text-[#0F172A] truncate">
              {current || 'Elige un archivo de la izquierda'}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              {dirty && puedeGuardarCodigo && (
                <span className="text-xs text-amber-700 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> sin guardar
                </span>
              )}
              {!puedeGuardarCodigo && (
                <span className="text-xs text-[#64748B]" data-testid="web-solo-lectura">
                  Solo puedes consultar el código
                </span>
              )}
              <Button size="sm" variant="outline" onClick={save}
                      disabled={!current || !dirty || saving || !puedeGuardarCodigo}
                      title={puedeGuardarCodigo ? undefined
                        : 'Guardar el código es del dueño de la cuenta: un archivo mal cerrado tira el sitio publicado.'}
                      data-testid="web-save-file">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        : <Save className="w-4 h-4 mr-1.5" />}
                Guardar
              </Button>
            </div>
          </div>
          <textarea
            ref={textareaRef}
            value={body}
            disabled={!current}
            onChange={(e) => { setBody(e.target.value); setDirty(true); }}
            onKeyDown={onKeyDown}
            spellCheck={false}
            aria-label={current ? `Contenido de ${current}` : 'Editor de archivo'}
            className="flex-1 w-full p-4 font-mono text-[13px] leading-relaxed text-[#0F172A]
                       outline-none resize-none min-h-[55vh] disabled:bg-[#F8FAFC]"
          />
        </section>
      </div>

      <p className="text-xs text-[#94A3B8]">
        Guarda con Ctrl+S (o Cmd+S). Usa <strong>Ver cómo va</strong> para revisar
        antes de publicar; en tu dominio nada cambia hasta que pulsas <strong>Publicar</strong>.
      </p>

      {preview && (
        <PreviewModal siteId={siteId} nonce={previewNonce} dirty={dirty}
                      onClose={() => setPreview(false)} />
      )}

      {dialogo && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             data-testid="web-dialogo"
             onClick={(e) => { if (e.target === e.currentTarget) setDialogo(null); }}>
          <form className="bg-white rounded-xl max-w-md w-full p-5 space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  const v = (dialogo.valor ?? '').trim();
                  if (dialogo.valor !== undefined && !v) return;
                  const fn = dialogo.onAceptar; setDialogo(null); fn(v);
                }}>
            <h2 className="text-lg font-bold text-[#0F172A]">{dialogo.titulo}</h2>
            {dialogo.descripcion && (
              <p className="text-sm text-[#475569]">{dialogo.descripcion}</p>
            )}
            {dialogo.valor !== undefined && (
              <input autoFocus className="w-full border border-[#CBD5E1] rounded-lg px-3 py-2
                                          font-mono text-sm" value={dialogo.valor}
                     placeholder={dialogo.marcador || ''}
                     onChange={(e) => setDialogo({ ...dialogo, valor: e.target.value })} />
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" size="sm" variant="ghost" onClick={() => setDialogo(null)}>
                Cancelar
              </Button>
              <Button type="submit" size="sm" data-testid="web-dialogo-ok"
                      className={dialogo.peligro ? 'bg-red-600 hover:bg-red-700'
                                                 : 'bg-[#F72585] hover:bg-[#D81B70]'}>
                {dialogo.boton || 'Aceptar'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {zipPendiente && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             data-testid="web-upload-confirm">
          <div className="bg-white rounded-xl max-w-md w-full p-5 space-y-4">
            <h2 className="text-lg font-bold text-[#0F172A]">
              {yaTieneSitio ? '¿Reemplazar tu sitio?' : '¿Subir tu sitio?'}
            </h2>
            <p className="text-sm text-[#475569]">
              Vamos a tomar el contenido de <strong>{zipPendiente.name}</strong>
              {yaTieneSitio && <> y a sustituir con él los archivos que tienes ahora</>}.
            </p>
            <p className="text-sm text-[#475569]">
              Tu sitio en internet <strong>no cambia</strong> hasta que pulses
              Publicar. Y si algo no te gusta, puedes volver a cualquiera de las
              últimas {MAX_VERSIONES} versiones publicadas.
            </p>
            <p className="text-xs text-[#94A3B8]">
              Si tu plantilla trae muchas imágenes puede tardar un par de minutos.
              No cierres esta pestaña.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setZipPendiente(null)}>
                Cancelar
              </Button>
              <Button size="sm" className="bg-[#F72585] hover:bg-[#D81B70]"
                      onClick={() => uploadZip(zipPendiente)}
                      data-testid="web-upload-confirm-ok">
                {yaTieneSitio ? 'Sí, reemplazar' : 'Sí, subir'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Vista previa del borrador.
 *
 * El iframe va en `sandbox` SIN `allow-same-origin` a propósito: adentro corre
 * el JavaScript del dueño del sitio, y en un origen opaco no puede tocar la
 * sesión del CRM ni llamar a la API en nombre de quien está mirando. El backend
 * manda la misma restricción por cabecera, por si el atributo se pierde.
 */
function PreviewModal({ siteId, nonce, dirty, onClose }) {
  const [dispositivo, setDispositivo] = useState('escritorio');
  const [base, setBase] = useState(null);
  const [error, setError] = useState(null);
  const [caja, setCaja] = useState({ w: 0, h: 0 });
  const cajaRef = useRef(null);

  // La dirección se pide firmada al abrir. Dentro del marco aislado el
  // navegador no manda la cookie de sesión, así que la autorización tiene que
  // ir en la ruta: de otro modo el HTML carga pero sus estilos e imágenes
  // vuelven con 401 y el sitio se ve en blanco.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data } = await api.get(`/tenant/web/sites/${siteId}/preview-token`);
        if (vivo) setBase(data.url);
      } catch (e) {
        if (vivo) setError(formatApiError(e.response?.data?.detail));
      }
    })();
    return () => { vivo = false; };
  }, [siteId, nonce]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Medir el hueco disponible para saber cuánto hay que reducir.
  useEffect(() => {
    const el = cajaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const medir = () => setCaja({ w: el.clientWidth, h: el.clientHeight });
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [base, error]);

  // `base` puede ser una ruta de aqui mismo o una direccion completa a
  // `vp.<dominio>`; el backend decide cual segun este dado de alta el DNS.
  const esOtroDominio = /^https?:\/\//i.test(base || '');

  const d = DISPOSITIVOS[dispositivo];
  // Se renderiza al ancho REAL del dispositivo y luego se reduce para que
  // quepa. Estirar el marco al ancho del recuadro era lo que deformaba la
  // vista de escritorio: el sitio creía estar en una pantalla angosta y
  // cambiaba a su diseño de tablet.
  const escala = caja.w
    ? Math.min(1, (caja.w - 24) / d.w, (caja.h - 24) / d.h)
    : 1;
  const pct = Math.round(escala * 100);

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/70 p-3 sm:p-6 flex flex-col"
         role="dialog" aria-modal="true" aria-label="Vista previa del sitio">
      <div className="bg-white rounded-xl overflow-hidden flex flex-col flex-1 max-w-7xl w-full mx-auto">
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[#E2E8F0]">
          <div className="min-w-0">
            <p className="font-semibold text-[#0F172A] text-sm">Así va tu sitio</p>
            <p className="text-xs text-[#94A3B8]">
              {dirty
                ? 'Ojo: tienes cambios sin guardar y aquí no se ven todavía.'
                : 'Es tu versión guardada. Nadie más la ve hasta que publiques.'}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {Object.entries(DISPOSITIVOS).map(([clave, cfg]) => {
              const Icono = cfg.icono;
              const activo = dispositivo === clave;
              return (
                <Button key={clave} size="sm" variant={activo ? 'outline' : 'ghost'}
                        onClick={() => setDispositivo(clave)}
                        title={`${cfg.nombre} — ${cfg.w}×${cfg.h}`}
                        aria-label={`Ver como ${cfg.nombre}`}
                        aria-pressed={activo}
                        data-testid={`web-preview-${clave}`}>
                  <Icono className="w-4 h-4" />
                </Button>
              );
            })}
            <Button size="sm" variant="ghost" onClick={onClose} aria-label="Cerrar">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div ref={cajaRef}
             className="flex-1 bg-[#F1F5F9] overflow-auto grid place-items-center p-3">
          {error && <p className="text-sm text-red-700 max-w-md text-center">{error}</p>}
          {!error && !base && <Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" />}
          {!error && base && (
            // El contenedor ocupa el tamaño YA REDUCIDO; el marco de adentro
            // conserva el tamaño real del dispositivo y solo se escala. Sin
            // esta pareja, el reducido deja un hueco fantasma alrededor.
            <div style={{ width: d.w * escala, height: d.h * escala }}>
              <iframe
                key={`${base}-${dispositivo}`}
                title={`Vista previa del sitio — ${d.nombre}`}
                src={base}
                // Si el sitio se sirve desde su propio dominio, ya esta
                // separado de verdad y el aislamiento forzado sobra: con el, el
                // marco vuelve a tener un origen opaco y el navegador rechaza
                // los modulos del propio sitio. Si se sirve desde aqui mismo,
                // el aislamiento es lo unico que separa y se queda.
                {...(esOtroDominio ? {} : { sandbox: 'allow-scripts allow-forms allow-popups' })}
                className="bg-white border border-[#E2E8F0] rounded shadow-sm"
                style={{
                  width: d.w, height: d.h, border: 0,
                  transform: `scale(${escala})`, transformOrigin: 'top left',
                }}
              />
            </div>
          )}
        </div>

        <div className="px-4 py-1.5 border-t border-[#E2E8F0] text-xs text-[#94A3B8]
                        flex items-center justify-between">
          <span>{d.nombre} · {d.w}×{d.h} px</span>
          {pct < 100 && <span>Reducido al {pct}% para que quepa en pantalla</span>}
        </div>
      </div>
    </div>
  );
}
